import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../env';
import {
  RecipeDishTypeOption,
  RecipeFacetsCatalogue,
  RecipeFacetsUpdateRequest,
} from './recipe-facets.models';

/**
 * Reads the recipe-facet catalogue (course/season/dish-type vocabularies) and
 * writes manual facet overrides. The catalogue is cached because it rarely
 * changes within a session and is needed to resolve dish-type keys → labels.
 */
@Injectable({ providedIn: 'root' })
export class RecipeFacetsService {
  private readonly http = inject(HttpClient);

  private catalogue$: Observable<RecipeFacetsCatalogue> | null = null;

  /** GET /recipe-facets — cached. Pass forceRefresh after admin vocab edits. */
  getCatalogue(forceRefresh = false): Observable<RecipeFacetsCatalogue> {
    if (forceRefresh || !this.catalogue$) {
      this.catalogue$ = this.http
        .get<RecipeFacetsCatalogue>(`${environment.apiUrl}/recipe-facets`)
        .pipe(shareReplay(1));
    }
    return this.catalogue$;
  }

  /** GET /recipe-dish-types — the dish-type vocabulary only. */
  getDishTypes(): Observable<RecipeDishTypeOption[]> {
    return this.http.get<RecipeDishTypeOption[]>(
      `${environment.apiUrl}/recipe-dish-types`,
    );
  }

  /**
   * PUT /recipes/:id/facets — manual override. Locks the recipe's facets so the
   * async classifier no longer touches them. `course: ''` clears the course.
   * Returns 204 (no body).
   */
  updateRecipeFacets(
    recipeId: number,
    body: RecipeFacetsUpdateRequest,
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/recipes/${recipeId}/facets`,
      body,
    );
  }

  /** Invalidate the cached catalogue (e.g. after dish-type vocab CRUD). */
  invalidateCatalogue(): void {
    this.catalogue$ = null;
  }
}
